'use client';

import React from 'react';

interface ScoreGaugeProps {
  score: number;
  maxScore?: number;
  color: string;
  change?: number;
}

/**
 * Semi-circular gauge displaying the reputation score.
 * Shows score from 0-1000 with color-coded indicator.
 */
function ScoreGauge({ score, maxScore = 1000, color, change }: ScoreGaugeProps) {
  const percentage = Math.min(score / maxScore, 1);

  return (
    <div className="flex flex-col items-center">
      {/* Gauge container */}
      <div className="relative h-24 w-48 overflow-hidden">
        {/* Background arc */}
        <div className="absolute inset-0 rounded-t-full border-8 border-gray-200 border-b-transparent" />

        {/* Colored progress arc */}
        <div
          className="absolute inset-0 rounded-t-full border-8 border-b-transparent transition-all duration-500"
          style={{
            borderColor: color,
            borderBottomColor: 'transparent',
            clipPath: `polygon(0 100%, 50% 0%, ${50 + percentage * 50}% 0%, ${percentage * 100}% 100%)`,
          }}
        />

        {/* Center content */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
          {change !== undefined && change !== 0 && (
            <p
              className="text-sm font-semibold"
              style={{ color }}
            >
              {change > 0 ? '+' : ''}{change}u
            </p>
          )}
          <p className="text-3xl font-bold">{score}</p>
        </div>
      </div>

      {/* Scale labels */}
      <div className="mt-2 flex w-48 justify-between text-xs text-gray-500">
        <span>0</span>
        <span className="text-sm font-medium text-gray-700">Reputation units</span>
        <span>{maxScore}</span>
      </div>
    </div>
  );
}

export default ScoreGauge;
